import {
	makeApiRequest,
	Auth,
	ApiRequestGetParams,
	ApiRequestGetParamsRaw,
	isApiRequestGetParams
} from "api/makeApiRequest";
import { ApiRequest } from "api/makeApiRequest/ApiRequest";
import { ValidationCallback } from "validation/validate";
import { addPostProcessingToResponse } from "api/utils/addPostProcessingToResponse";

export type DataFetcher<RawGetData extends ApiRequestGetParamsRaw, Output> = (
	data: RawGetData,
	auth: Auth
) => ApiRequest<Output>;

type Opts = {
	waitForTimezone?: boolean;
	cacheInMemory?: boolean;
};

export const makeFetchData = <
	RawGetData extends ApiRequestGetParamsRaw,
	Output = {},
	RawApiOutput = Output
>(
	endPoint: string,
	flagValidationErrors: ValidationCallback<RawApiOutput> = (input, flag) => {},
	preProcessor?: (data: RawGetData) => ApiRequestGetParams,
	postProcessor?: (
		arg: RawApiOutput | undefined,
		requestPayload: ApiRequestGetParamsRaw
	) => Output,
	opts?: Opts
) => {
	const dataFetcher = (
		data: RawGetData,
		auth: Auth,
		cacheInMemory?: boolean
	) => {
		const get = preProcessor ? preProcessor(data) : data;
		if (!isApiRequestGetParams(get)) {
			throw new Error(
				"Invalid get data - consider adding a preProcessor to convert it to the correct format"
			);
		}

		const req = makeApiRequest<RawApiOutput>(endPoint, {
			data: {
				get
			},
			flagValidationErrors,
			auth,
			waitForTimezone:
				opts === undefined || opts.waitForTimezone === undefined
					? true
					: opts.waitForTimezone,
			cacheInMemory: cacheInMemory
				? cacheInMemory
				: opts === undefined || opts.cacheInMemory === undefined
				? false
				: opts.cacheInMemory
		});

		if (postProcessor) {
			return addPostProcessingToResponse(req, postProcessor, data);
		}

		return (req as unknown) as ApiRequest<Output>;
	};

	return dataFetcher;
};
